import { cs } from '@bbkAdminUtils/css-utils';
import { ButtonV2 } from '@bbkAdminComponents/buttons';
import icons from '@bbkAdminUtils/icons';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import React, { CSSProperties } from 'react';
import tailwindColors from '@bbkShared/tailwind.colors';
import { ReactFCC } from '@bbkAdminUtils/utility-types';

export const SettingsPageMedWrapper = styled.div`
  max-width: 550px;
`;
export const SettingsPageWrapper = styled.div`
  max-width: 900px;
`;

export const PageHeader = styled.div`
  font-weight: bold;
  font-size: 20px;
  line-height: 30px;
  color: ${({ link }: { link?: boolean }) =>
    link ? tailwindColors.cbrprimary['600'] : '#000000'};
`;

export const PageHeaderBreadcrumbGroup = styled.div`
  display: flex;
  align-items: center;
  padding-top: 4px;

  > * {
    margin-right: 1rem;
  }
`;

export const BreadCrumpSeparator = () => (
  <svg
    width="8"
    height="13"
    viewBox="0 0 8 13"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      opacity="0.25"
      d="M2.21558 0.879878C1.70847 0.373374 0.886989 0.373374 0.379878 0.879878C-0.126626 1.38699 -0.126626 2.20847 0.379878 2.71558L4.16616 6.5L0.379878 10.2844C-0.126626 10.7915 -0.126626 11.613 0.379878 12.1201C0.886989 12.6266 1.70847 12.6266 2.21558 12.1201L6.91762 7.41809V7.41748C7.42412 6.91097 7.42412 6.09011 6.91762 5.58364L2.21558 0.879878Z"
      fill="black"
    />
  </svg>
);

export const HeaderItem = styled.div`
  font-weight: bold;
  font-size: 16px;
  color: #000000;
  margin-bottom: 1rem;
  margin-top: ${({ first }: { first?: boolean }) => (first ? 0 : '1.5rem;')};
`;

const RowItemStyles = `
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  margin-bottom: 0.7rem;
  font-weight: 500;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.5);
`;

type RowItemProps = {
  spaceBetween?: boolean;
  hover?: boolean;
};
export const RowItem = styled.div`
  ${RowItemStyles}
  padding: 1rem 1.2rem;
  display: flex;
  align-items: center;
  color: rgba(0, 0, 0, 0.5);

  ${({ spaceBetween }: RowItemProps) =>
    spaceBetween && 'justify-content: space-between;'}
  &:hover {
    ${({ hover }: RowItemProps) => hover && 'cursor: pointer;'}
  }
`;

export const GroupItem = styled.div`
  ${RowItemStyles}
  padding: 1rem 1.2rem;
  margin-bottom: 1rem;
`;

export const PlanGroupItem = styled.div`
  ${RowItemStyles}
  margin-bottom: 1rem;
  border: 0;
  box-shadow: 0 3px 2px #e5e7e9;
  border-radius: 6px;
  padding: 2.25rem 3rem 2rem 3rem;
  color: black;
  font-weight: 400;

  .section-header {
    display: block;
    margin-bottom: 1rem;
    font-size: 10px;
    text-transform: uppercase;
    color: #9d9db8;
  }
`;

export const CollapseRowGroup = styled.div`
  ${RowItemStyles}
  padding-top: 0.3rem;
  ${({ inactive }: RowCollapseItemProps) =>
    inactive &&
    css`
      background: rgba(0, 0, 0, 0.1);
      cursor: not-allowed;
    `}
`;

type RowCollapseItemProps = {
  last?: boolean;
  inactive?: boolean;
};
export const RowCollapseItem = styled.div<RowCollapseItemProps>`
  ${({ last }) =>
    !last &&
    css`
      border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    `}
  ${({ inactive }) =>
    inactive &&
    css`
      /* background: rgba(0, 0, 0, 0.1); */
      cursor: not-allowed;
    `}
`;

export const CollapseHeaderGroup = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem 1.2rem;
`;

export const CollapseBodyGroup = styled.div`
  background: rgba(0, 0, 0, 0.03);
  padding: 1rem 3.2rem;
`;

export const CollapseGroupArrow: ReactFCC<{
  active: boolean;
  styles?: CSSProperties;
}> = ({ active, styles }) => (
  <ButtonV2
    circle
    small
    style={styles}
    className={cs('tw-mr-2 tw-transition-transform [&_svg]:!tw-w-auto', {
      '-tw-rotate-90': !active,
    })}
  >
    {icons.chevronDown}
  </ButtonV2>
);

type MainTitleProps = {
  center?: boolean;
  hover?: boolean;
};
export const MainTitle = styled.div`
  font-weight: bold;
  font-size: 14px;
  color: ${tailwindColors.cbrprimary['600']};

  ${({ center }: MainTitleProps) =>
    center &&
    css`
      display: flex;
      align-items: center;
    `}
  &:hover {
    ${({ hover }: MainTitleProps) =>
      hover &&
      css`
        cursor: pointer;
      `}
  }
`;

export const Description = styled.div`
  font-weight: 500;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.5);
`;

export const InputGroup = styled.div`
  display: grid;
  grid-template-columns: 50% 50%;
  margin-bottom: 1rem;
  ${({ last }: { last?: boolean }) =>
    !last &&
    css`
      border-bottom: 1px solid rgba(0, 0, 0, 0.1);
      padding-bottom: 1rem;
    `}
`;
export const InputGroupItemDesc = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

export const SplitGroup = styled.div`
  display: grid;
  grid-template-columns: 50% 50%;
  gap: 1rem;
`;

export const DescriptionTitle = styled.div`
  font-weight: bold;
`;

export const ConnectedItem = () => (
  <span>
    {icons.check}
    <span
      css={{ marginLeft: '0.3rem', marginRight: '0.5rem', color: '#10a069' }}
    >
      connected
    </span>
  </span>
);
export const UnconnectedItem = () => (
  <span css={{ color: '#f32b43', path: { fill: 'currentColor' } }}>
    {icons.warning}
    <span css={{ marginLeft: '0.3rem', marginRight: '0.5rem' }}>
      Not connected
    </span>
  </span>
);

export const RowLinkButton: React.FC<React.ComponentProps<typeof ButtonV2>> = ({
  children,
  ...other
}) => (
  <ButtonV2 link centerAll {...other}>
    {children}
    <span className={cs({ 'tw-pl-1': Boolean(children) })}>
      <svg
        width="15"
        height="14"
        viewBox="0 0 15 14"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M14.0877 6.15301L14.1143 6.19078L14.1013 6.17053L14.0877 6.15301ZM9.12691 0.478082C8.65692 -0.0590469 7.86713 -0.15056 7.29073 0.238995L7.15139 0.346405C6.61426 0.816394 6.5227 1.60614 6.91226 2.18253L7.01969 2.3219L9.88631 5.59999L1.4 5.59999C0.682028 5.59999 0.0902916 6.1405 0.00942076 6.83677L0 6.99998C0 7.77318 0.6268 8.39998 1.4 8.39998H9.88631L7.01969 11.6781C6.5497 12.2152 6.5638 13.0102 7.02642 13.5297L7.15139 13.6536C7.68852 14.1236 8.48346 14.1095 9.00305 13.6469L9.12691 13.5219L14.0176 7.93266L14.037 7.91027C14.0479 7.89758 14.0585 7.88465 14.069 7.87156L14.0176 7.93266C14.0594 7.88598 14.0975 7.83705 14.132 7.78628C14.1436 7.76898 14.1553 7.7509 14.1665 7.73262L14.2139 7.64948L14.2406 7.59565C14.258 7.55867 14.2737 7.52106 14.2876 7.48294C14.2918 7.47179 14.2954 7.46167 14.2989 7.4515C14.3129 7.40976 14.3254 7.36646 14.3357 7.32281C14.3381 7.31305 14.3403 7.30336 14.3424 7.29359C14.3504 7.25551 14.3569 7.21714 14.3619 7.17857C14.3694 7.12034 14.3733 7.06059 14.3733 6.99998L14.3635 7.15114L14.3667 7.13662C14.3707 7.09458 14.3729 7.05255 14.3733 7.01049V6.98947C14.3729 6.94742 14.3707 6.90545 14.3666 6.86361L14.3651 6.84771C14.3641 6.83882 14.3631 6.82998 14.362 6.82114L14.3647 6.84378C14.3594 6.79786 14.352 6.75198 14.3422 6.70647C14.3403 6.69671 14.3381 6.68689 14.3358 6.67716C14.3254 6.63347 14.3129 6.59025 14.2984 6.54762C14.2954 6.53834 14.2918 6.52825 14.2881 6.51813C14.2737 6.47897 14.258 6.44138 14.2406 6.4044L14.2343 6.39116C14.2321 6.38647 14.2298 6.3818 14.2274 6.37714C14.2257 6.37363 14.2237 6.36967 14.2217 6.36569L14.2139 6.35048L14.1666 6.26726C14.1547 6.24853 14.149 6.23952 14.1431 6.2306L14.132 6.21368L14.1143 6.19078L14.1213 6.19915L14.1026 6.17232C14.0944 6.16117 14.086 6.15015 14.0775 6.13925L14.0877 6.15301L14.0619 6.11934L14.0359 6.08995L14.0269 6.07807L14.0331 6.08722L14.0163 6.0662C13.9992 6.04731 13.9886 6.03595 13.9777 6.02475L13.9827 6.02979L9.12691 0.478082ZM13.8952 5.9464L13.9269 5.97494C13.9227 5.97111 13.9186 5.96735 13.9144 5.96357L13.8952 5.9464Z"
          fill={tailwindColors.cbrprimary['600']}
        />
      </svg>
    </span>
  </ButtonV2>
);

export const RowStyles = {
  fiftyPercentColumn: {
    flexBasis: '50%',
  },
  fortyPercentColumn: {
    flexBasis: '40%',
  },
  flexColumn: {
    flex: 1,
  },
  centerAll: {
    display: 'flex',
    alignItems: 'center',
  },
  iconSpacer: '0.5rem',
  paragraphSpacer: { marginBottom: '0.5rem' },
};
